import { createEntry } from '@atlassian/jira-route-entry/src/createEntry.tsx';
import { classicSoftwareBacklogRoute } from '@atlassian/jira-router-routes-software-classic-backlog-routes/src/classicSoftwareBacklogRoute.tsx';
import { RapidBoardSpaPage } from '@atlassian/jira-router-routes-user-board-entries/src/ui/async.tsx';
import { NextGenBacklogSkeleton } from '@atlassian/jira-skeletons/src/ui/next-gen-backlog/NextGenBacklogSkeleton.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { composeLayouts } from '@atlassian/jira-route-layout/src/GlobalComponent.tsx';
import { genericProjectLayout } from '@atlassian/jira-generic-project-layout/src/index.tsx';
import { pageLoadIssuePreloadLayoutBuilder } from '@atlassian/jira-issue-preload-layout/src/index.tsx';
import { MENU_ID, SIDEBAR_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { AsyncHorizontalNavJira } from '@atlassian/jira-horizontal-nav/src/async.tsx';
import { getNavigationResources } from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { incidentExperimentTraitResource } from '@atlassian/jira-router-resources-software-incidents/src/services/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import {
	forgeProjectModuleResource,
	forgeBacklogModuleResource,
	forgeSprintModuleResource,
} from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
	getUIFBacklogDataResource,
} from '@atlassian/jira-router-resources-classic-projects/src/services/main.tsx';
import { epicsLiteResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/epics-lite/epics-lite-resource.tsx';
import { getEditModelResource } from '@atlassian/jira-router-resources-classic-projects/src/services/edit-model/index.tsx';
import { getBoardDetailsResource } from '@atlassian/jira-router-resources-classic-projects/src/services/board-details/index.tsx';
import { LazyIssueApp } from '@atlassian/jira-lazy-issue-app/src/LazyIssueApp.tsx';
import { LazyAtlassianNavigation } from '@atlassian/jira-atlassian-navigation/src/async.tsx';
import { AGGRESSIVE_EARLY_ASSET_STRATEGY } from '@atlassian/jira-common-constants/src/ssr-early-assets.tsx';
import { ForPaintClassicSoftwareProjectSidebar as AsyncForPaintSidebarSoftwareClassic } from '@atlassian/jira-navigation-apps-sidebar-software-classic/src/async.tsx';
import { resourceWithCondition } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import { projectThemeResource } from '@atlassian/jira-router-resources-project-theme/src/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { aiEntitlementResource } from '@atlassian/jira-router-resources-ai-entitlement/src/services/index.tsx';
import { backlogCMPViewSettingsResource } from '@atlassian/jira-router-resources-uif-backlog';
import { IS_MODERN_SSR } from '@atlassian/jira-conditional-ssr/src/constants.tsx';
import { classicBacklogPageEntryPoint } from './ui/page-entry-point/index.tsx';
import { classicBacklogCapability } from './common/constants.tsx';
import { LazyBacklogPage } from './ui/LazyBacklogPage.tsx';

export const classicSoftwareBacklogRouteEntry = createEntry(classicSoftwareBacklogRoute, () => ({
	group: 'classic-software',
	ufoName: 'classic-backlog',
	component: fg('clean_up_route_entry_component_field') ? undefined : RapidBoardSpaPage,
	skeleton: NextGenBacklogSkeleton,
	layout: composeLayouts(
		genericProjectLayout,
		pageLoadIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }], 'SIDEBAR'),
	),
	navigation: {
		menuId: MENU_ID.PROJECTS,
		sidebar: AsyncForPaintSidebarSoftwareClassic,
		horizontal: AsyncHorizontalNavJira,
		sidebarId: SIDEBAR_ID.CONTENT,
	},
	resources: [
		...getNavigationResources({
			isUsingSidebarEntrypoint: true,
		}),
		// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
		incidentExperimentTraitResource,
		projectContextResource,
		forgeProjectModuleResource,
		staticSidebarShortcutsResource,
		staticProjectSidebarNavigationResource,
		staticProjectSidebarConnectAddonsNavigationResource,
		// end of resources expanded from getNavigationSidebarProjectResource
		...getUIFBacklogDataResource(),
		...getRapidboardResources(),
		getRapidBoardDataResource(),
		getEditModelResource(),
		getBoardDetailsResource(),
		forgeBacklogModuleResource,
		forgeSprintModuleResource,
		resourceWithCondition(() => getIsSoftwareThemingEnabled(), projectThemeResource),
		aiEntitlementResource,
		resourceWithCondition(
			() =>
				expVal('backlog-preload-epics-ssr', 'isEnabled', false) &&
				(!IS_MODERN_SSR || fg('enable_ssr_for_cmp_backlog')),
			epicsLiteResource,
		),
		resourceWithCondition(
			() => fg('backlog-hydrate-view-settings-from-backend'),
			backlogCMPViewSettingsResource,
		),
	],
	afterPaint: [LazyIssueApp],
	forPaint: [LazyAtlassianNavigation, LazyBacklogPage],
	preloadOptions: {
		earlyChunks: ['software-cmp-backlog-early-entry'],
	},
	meta: {
		capability: classicBacklogCapability,
	},
	earlyAssets: AGGRESSIVE_EARLY_ASSET_STRATEGY,
	bifrost: {
		bifrostGroup: 'backlog-and-board',
		bundling: {
			packageName:
				'@atlassian/jira-router-routes-software-classic-backlog-entries/src/classicSoftwareBacklogRouteEntry.tsx',
			whichExportToUse: 'classicSoftwareBacklogRouteEntry',
		},
		enabled: true,
		manifestName: 'jira-spa-software-backlog.json',
	},
	entryPoint: () => classicBacklogPageEntryPoint,
	entryPointOptions: fg('ssr_placeholder_on_topnav_and_sidebar_for_backlog')
		? { isSSRPlaceholderEnabled: true }
		: undefined,
	supportsPreviewPanel: true,
	isHydrateRootEnabled: fg('cmp-backlog-hydrate-root'),
	excludeFromWrmDeferredLoading: true,
}));
